import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import { Palette } from "../AppConfig";
import { saveJobBasic, deepCopy } from "../components/Common";
import IconButton from "@material-ui/core/IconButton";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import {
    changePage,
    CLEAR_ALL_DESIGNER_MESSAGE,
    MAIN_PAGE,
    SHOW_CUSTOMER_JOB_PANEL,
    toggleDesigner,
} from "../redux_store/appState";
import CreateIcon from "@material-ui/icons/Create";
import UndoIcon from "@material-ui/icons/Undo";
import RedoIcon from "@material-ui/icons/Redo";
import RefreshIcon from "@material-ui/icons/Refresh";
import WebIcon from "@material-ui/icons/Web";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CrossCircleIcon from "@material-ui/icons/Cancel";
import CachedIcon from "@material-ui/icons/Cached";
import * as Paper from "./layout/PaperFunctions";
import BSL_Button from "../components/BSL_Button";
import { updateLayout2D, updateLayout3D, setToolSetArea } from "../redux_store/appStateHelper";
import {
    THREED_MODE,
    THREED_MINI_MODE,
    UNDO,
    REDO,
    TOOL_DRAWER_HIDE,
    BEAMS_AND_POSTS_TOOL,
} from "../redux_store/appState";

const useStyles = makeStyles((theme) => ({
    root: {
        width: "100%",
        height: "86px",
        zIndex: "9998",
        backgroundColor: theme.brand.LYSAGHT ? Palette.Lysaght.LightGrey : "#F4F4F4",
        position: "absolute",
        borderBottom: "1px solid #acc2d1",
        paddingTop: "48px",
        display: "flex",
        position: "fixed",
        top: "0px",
        width: "100%",
        paddingRight: "0px !important",
    },
    icon: {
        paddingRight: "10px",
        width: "34px",
        height: "34px",
    },
    iconButton: {
        fontSize: theme.brand.FIELDERS ? "14px" : "14px",
        fontFamily: theme.typography.fontFamilySecondary,
        fontWeight: "400",
        color: theme.palette.primary.main,
        marginTop: "-4px",
        borderRadius: "0%",
        textTransform: theme.brand.FIELDERS ? "uppercase" : "capitalize",
    },
    iconButtonRoot: {
        padding: "4px 12px",
    },
    jobName: {
        fontSize: theme.brand.FIELDERS ? "20px" : "18px",
        fontFamily: theme.typography.fontFamily,
        fontWeight: theme.brand.FIELDERS ? "400" : "600",
        color: Palette.Control.DarkGrey,
        flex: "1",
        alignSelf: "center",
        cursor: "pointer",
        textTransform: theme.brand.FIELDERS ? "none" : "uppercase",
    },
    tools: {
        display: "flex",
        alignItems: "center",
        fontSize: "20px",
        fontFamily: theme.typography.fontFamily,
        fontWeight: "400",
        color: Palette.Control.DarkGrey,
        alignSelf: "center",
        paddingRight: "10px",
    },
    selected: {
        background: theme.brand.FIELDERS ? "#1a3061" : Palette.Lysaght.DarkBlue,
        color: "white",
    },
    status: {
        marginRight: "10px",
        width: "24px",
        height: "34px",
    },
}));

function useGlobalUndoRedo(onUndo, onRedo) {
    React.useEffect(() => {
        function handleKeyDown(e) {
            // Normalize key (Chrome uses 'z', Safari sometimes 'Z')
            const key = e.key.toLowerCase();

            if (e.ctrlKey && key === "z") {
                e.preventDefault();
                onUndo();
            }

            if (e.ctrlKey && key === "y") {
                e.preventDefault();
                onRedo();
            }

            // Optional: support Ctrl+Shift+Z (common redo shortcut)
            if (e.ctrlKey && e.shiftKey && key === "z") {
                e.preventDefault();
                onRedo();
            }
        }

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [onUndo, onRedo]);
}

export default function HeaderBar(props) {
    const classes = useStyles();
    const dispatch = useDispatch();

    const job = useSelector((state) => state?.job);
    const app = useSelector((state) => state?.app);

    const engineeringStatus = props?.engineeringStatus || "unknown";
    const engineeringLoading = props?.engineeringLoading || false;
    const setEngineeringLoading = props?.setEngineeringLoading;
    const HAS_UNDO = app?.job_history_buffer?.pointer > 0 && app?.job_history_buffer?.data?.length > 0;
    const HAS_REDO = app?.job_history_buffer?.pointer < app?.job_history_buffer?.data?.length - 1;
    const THREED_mode = app.designer[THREED_MODE];
    const threed_mini_mode = app.designer[THREED_MINI_MODE];
    const TOOL_DRAWER_HIDE_mode = app.designer[TOOL_DRAWER_HIDE];
    const SHOW_LOADING_BACKDROP = app.designer.SHOW_LOADING_BACKDROP;
    const toolDrawerArea = app?.designer?.toolDrawerArea || 0;

    let job_title = <i>Untitled Job</i>;

    if (job?.Customer?.JobName) {
        job_title = job?.Customer?.JobName + " (#" + job?.id + ")";
    }

    const handleClickCheckEngineering = async () => {
        setEngineeringLoading(true);
        await dispatch(setToolSetArea(toolDrawerArea, BEAMS_AND_POSTS_TOOL));
        setEngineeringLoading(false);

        window.Intercom("trackEvent", "Check Engineering Clicked", {
            job_id: job?.id,
            created_at: Math.floor(Date.now() / 1000),
        });
    };

    useGlobalUndoRedo(
        () => (HAS_UNDO ? dispatch(UNDO()) : () => {}),
        () => (HAS_REDO ? dispatch(REDO()) : () => {})
    );

    return (
        <div className={classes.root}>
            <IconButton
                className={classes.iconButton}
                classes={{ root: classes.iconButtonRoot }}
                onClick={() => {
                    if (!job) return;
                    var save_job = deepCopy(job);

                    if (app?.user && save_job)
                        //&& !app?.user?.id.includes("diego.portela"))
                        save_job.LastEditor = {
                            id: app.user.id,
                            DisplayName: app.user.DisplayName,
                            UserCode: app.user.UserCode,
                            _session_id: "",
                            _ts: Date.now(),
                        };

                    saveJobBasic(save_job, false);
                    window.svg = null;
                    window.svg_data = null;
                    window.job = null;
                    Paper.ClearAll();

                    dispatch({ type: CLEAR_ALL_DESIGNER_MESSAGE });
                    dispatch(changePage(MAIN_PAGE));
                }}
            >
                <ChevronLeftIcon className={classes.icon} /> Back to Jobs
            </IconButton>
            <div
                className={classes.jobName}
                onClick={() => {
                    if (job && !SHOW_LOADING_BACKDROP) dispatch(toggleDesigner(SHOW_CUSTOMER_JOB_PANEL));
                }}
            >
                {job_title}
                <IconButton className={classes.iconButton} classes={{ root: classes.iconButtonRoot }}>
                    <CreateIcon className={classes.icon} /> Edit Details
                </IconButton>
            </div>
            <div className={classes.tools}>
                <IconButton
                    className={classes.iconButton}
                    classes={{ root: classes.iconButtonRoot }}
                    onClick={handleClickCheckEngineering}
                    disabled={engineeringLoading}
                >
                    {!engineeringStatus || engineeringStatus === "unknown" || engineeringLoading ? (
                        <CachedIcon
                            className={classes.status}
                            style={{ animation: engineeringLoading ? "spin 1s linear infinite" : "", color: "#b37f0b" }}
                        />
                    ) : engineeringStatus === "pass" ? (
                        <CheckCircleIcon className={classes.status} style={{ color: "#008200" }} />
                    ) : (
                        <CrossCircleIcon className={classes.status} style={{ color: "#8c1212" }} />
                    )}
                    <span style={{ paddingTop: "3px" }}>CHECK ENGINEERING</span>
                </IconButton>
                <IconButton
                    className={classes.iconButton}
                    classes={{ root: classes.iconButtonRoot }}
                    disabled={!HAS_UNDO}
                    onClick={() => (HAS_UNDO ? dispatch(UNDO()) : () => {})}
                >
                    <UndoIcon className={classes.icon} /> Undo
                </IconButton>
                <IconButton
                    className={classes.iconButton}
                    classes={{ root: classes.iconButtonRoot }}
                    disabled={!HAS_REDO}
                    onClick={() => (HAS_REDO ? dispatch(REDO()) : () => {})}
                >
                    <RedoIcon className={classes.icon} /> Redo
                </IconButton>
                <IconButton
                    className={classes.iconButton}
                    classes={{ root: classes.iconButtonRoot }}
                    onClick={() => {
                        if (job && !SHOW_LOADING_BACKDROP) {
                            if (THREED_mode) dispatch(updateLayout3D());
                            if (!THREED_mode || (THREED_mode && threed_mini_mode)) dispatch(updateLayout2D());
                        }
                    }}
                >
                    <RefreshIcon className={classes.icon} /> Refresh
                </IconButton>
                <IconButton
                    className={classes.iconButton + (TOOL_DRAWER_HIDE_mode ? "" : " " + classes.selected)}
                    classes={{ root: classes.iconButtonRoot }}
                    onClick={() => {
                        dispatch(toggleDesigner(TOOL_DRAWER_HIDE));
                    }}
                >
                    <WebIcon className={classes.icon} /> Properties
                </IconButton>
            </div>
        </div>
    );
}
